import PropTypes from "prop-types";
import { useEffect } from "react";

import { Controller, useFormContext } from "react-hook-form";

import { Grid, Paper } from "@mui/material";

import { CustomerSelect } from "@/features/customers";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { CustomDatePicker, CustomInputField } from "@/features/ui";
import { OutletWarehouseSelect } from "@/features/warehouses";

export const GeneralInformationSection = ({ editMode = false }) => {
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();

  const seller = watch("seller");
  const buyerableType = watch("buyerableType");
  const customerClaimProductVariants = watch("customerClaimProductVariants");

  // Disable fields when there are existing products
  const disableFields = customerClaimProductVariants.length > 0;

  // Auto-set buyerableType based on seller's role
  useEffect(() => {
    if (seller) {
      const buyerableType =
        seller.role === OutletRoleEnum.RETAIL ? "CUSTOMER" : "OUTLET";
      setValue("buyerableType", buyerableType);
    }
  }, [seller, setValue]);

  // Auto-clear buyer and warehouse when seller changes
  useEffect(() => {
    if (editMode) return;
    setValue("buyer", null);
    setValue("warehouse", null);
  }, [seller, editMode, setValue]);

  // Auto-clear buyer when buyerableType changes
  useEffect(() => {
    if (editMode) return;
    if (buyerableType) {
      setValue("buyer", null);
    }
  }, [buyerableType, editMode, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) =>
              buyerableType === "CUSTOMER" ? (
                <CustomerSelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={editMode || disableFields || !buyerableType}
                  TextFieldProps={{
                    label: "Pelanggan (Customer)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  allowedRoles={[OutletRoleEnum.RETAIL]}
                  disabled={editMode || disableFields || !buyerableType}
                  TextFieldProps={{
                    label: "Pelanggan (Outlet Retail)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode || disableFields}
                TextFieldProps={{
                  label: "Pemasok",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal"
                error={fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
          <Controller
            control={control}
            name="warehouse"
            render={({ field, fieldState }) => (
              <OutletWarehouseSelect
                value={field.value}
                onChange={field.onChange}
                outletId={seller?.id}
                disabled={editMode || disableFields || !seller}
                TextFieldProps={{
                  label: "Gudang Pemasok",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
          <CustomInputField
            name="note"
            label="Catatan"
            register={register}
            error={Boolean(errors.note)}
            helperText={errors.note?.message}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
